/**
 * The river meadows south of the quay (lifted from lantern-water's
 * `backdrop-south.js`): hedgerows and field gates along the towpath,
 * allotments, a shed, a scarecrow, straw bales, upturned skiffs, nets drying,
 * low cottages and trees framing the bottom of the view, an orchard corner
 * with beehives. Near the quay everything stays knee-high (a point at height h
 * covers the ground 1.9·h nearer the town). Standing on the river banks'
 * height (`riverBankHeight`). A world-coordinate set piece: placed at the
 * origin. Its own seed (31877); nothing here is walkable. No params.
 */
import * as THREE from 'three';
import { CELLS as C, atlasMesh, backdropMaterial } from '../backdrop-art.ts';
import { TINTS, createBackdropBuilder } from '../backdrop-kit.ts';
import { createBackdropProps } from '../backdrop-props.ts';
import { riverBankHeight } from '../river-course.ts';
import type { Prefab } from './types.ts';

export const prefab: Prefab = {
  id: 'backdrop_south',
  buildPrefab(ctx) {
    const p = new THREE.Group();
    p.name = 'backdrop-south';
    const { m, box, cylinder, line } = ctx;
    const material = backdropMaterial(ctx.kit, m.windowCool);
    const b = createBackdropBuilder(ctx, 31877);
    const P = createBackdropProps(ctx, b);
    const { kit } = b;
    const g = (x: number, z: number): number => riverBankHeight(x, z, x > 7);

    // Hedgerows with gaps, on the far side of the towpath.
    for (const [x0, x1] of [
      [-20.5, -15.4],
      [-13.4, -8.2],
      [16.2, 23.4],
      [25.6, 30.5],
    ] as const)
      P.hedge(p, (x0 + x1) / 2, g((x0 + x1) / 2, 20.9) - 0.05, 20.9, x1 - x0, 0.75, 0.75);
    // Field gates where the cart tracks leave the towpath.
    for (const x of [-14.4, 24.5]) {
      const y = g(x, 20.9);
      for (const s of [-1, 1]) box(p, x + s * 0.95, y + 0.5, 20.9, 0.14, 1.0, 0.14, m.darkWood);
      for (const yy of [0.35, 0.62, 0.88]) box(p, x, y + yy, 20.9, 1.8, 0.07, 0.05, m.wood);
      line(p, [x - 0.9, y + 0.35, 20.9], [x + 0.9, y + 0.88, 20.9], 0.05, m.wood);
    }

    // West meadow: allotments, straw, a scarecrow, cottages beyond.
    for (const [x, z, cell] of [
      [-11.4, 22.6, C.vegRows],
      [-8.8, 22.6, C.cabbage],
      [-11.4, 25.2, C.cabbage],
      [-8.8, 25.2, C.vegRows],
      [-17.6, 23.2, C.vegRows],
    ] as const)
      P.vegPlot(p, x, g(x, z), z, 2.2, 2.2, cell);
    P.shed(p, -6.3, g(-6.3, 23.2), 23.2, 1.6, 1.2, 'south');
    P.barrel(p, -6.2, g(-6.2, 24.4), 24.4);
    {
      // Scarecrow: a cross, a hat and a sampled scrap of laundry for the coat.
      const x = -10.1;
      const z = 23.9;
      const y = g(x, z);
      box(p, x, y + 0.8, z, 0.07, 1.6, 0.07, m.darkWood);
      box(p, x, y + 1.2, z, 1.0, 0.06, 0.06, m.darkWood);
      kit.quad([x - 0.28, y + 0.72, z + 0.05], [x + 0.28, y + 0.72, z + 0.05], [x + 0.3, y + 1.28, z + 0.05], [x - 0.3, y + 1.28, z + 0.05], C.laundry, [0.8, 0.8, 0.8], [0.5, 0, 0.74, 0.7]);
      cylinder(p, x, y + 1.48, z, 0.12, 0.2, m.canvas);
      cylinder(p, x, y + 1.39, z, 0.26, 0.03, m.canvas);
    }
    // Straw bales stacked by the track.
    for (const [x, z, yy] of [
      [-16.6, 25.6, 0],
      [-15.6, 25.6, 0],
      [-16.1, 25.6, 0.5],
      [-15.8, 26.5, 0],
    ] as const)
      kit.box(x, g(x, z) + yy + 0.25, z, 0.95, 0.5, 0.6, { top: C.straw, front: C.straw, east: C.straw, west: C.straw, back: null }, [1, 1, 1], [0.7, 1]);
    // Upturned skiffs along the bank.
    for (const [x, z, rot] of [
      [-0.8, 22.4, 0.3],
      [-2.8, 25.2, -0.2],
      [14.6, 22.4, -0.4],
    ] as const) {
      const hull = new THREE.Group();
      hull.position.set(x, g(x, z) + 0.2, z);
      hull.rotation.y = rot;
      p.add(hull);
      box(hull, 0, 0.12, 0, 0.9, 0.24, 2.3, m.darkWood);
      box(hull, 0, 0, 0, 1.0, 0.1, 2.1, m.teal);
    }
    // Nets and sacking drying on lines by the landing stages.
    P.laundry(p, -4.6, 21.0, -4.6, 23.2, g(-4.6, 22), 3);
    P.laundry(p, 18.6, 22.0, 20.8, 22.0, g(19.7, 22), 3);
    // Cottages that frame the bottom of the view: low eaves, big roofs.
    for (const [x, z, bays, deep, tint, rm, roof] of [
      [-16.8, 29.2, 3, 2, TINTS.straw, 'roof', 'gableX'],
      [-12.6, 30.6, 2, 3, TINTS.ochre, 'slate', 'gableZ'],
      [27.0, 30.6, 3, 2, TINTS.sage, 'roof', 'gableX'],
      [31.8, 28.4, 2, 3, TINTS.rust, 'slate', 'gableZ'],
    ] as const)
      b.house(p, {
        x,
        z,
        bays,
        deep,
        floors: 1,
        base: g(x, z) - 0.05,
        style: bays === 2 ? 'timber' : 'plaster',
        tint,
        roof,
        rise: 1.7,
        roofMat: rm === 'slate' ? m.roofDark : m.roof,
        face: 'south',
        side: 'east',
        stoneGround: false,
        dormers: false,
        lit: 0.25,
      });
    for (const [x, z, s, a] of [
      [-20.2, 26.4, 1.0, false],
      [-7.1, 27.6, 1.1, true],
      [-13.8, 27.9, 0.85, false],
      [20.5, 26.8, 0.95, false],
      [25.0, 26.8, 1.05, true],
      [30.8, 25.0, 1.1, false],
    ] as const)
      P.tree(p, x, g(x, z), z, s, a);

    // East meadow: an orchard corner with beehives and a washing line.
    for (const [x, z] of [
      [19.6, 23.4],
      [20.5, 23.5],
      [21.4, 23.3],
    ] as const) {
      const y = g(x, z);
      kit.box(x, y + 0.32, z, 0.55, 0.55, 0.55, { top: null, front: C.planks, east: C.planks, west: C.planks, back: null }, [1, 0.95, 0.85]);
      box(p, x, y + 0.66, z, 0.7, 0.07, 0.7, m.roofDark);
      box(p, x, y + 0.05, z, 0.6, 0.1, 0.6, m.stone);
    }
    P.laundry(p, 23.0, 25.0, 26.4, 25.0, g(24, 25), 4);
    P.vegPlot(p, 28.4, g(28.4, 23.2), 23.2, 2.4, 2.2, C.cabbage);
    P.woodpile(p, 17.2, g(17.2, 23.8), 23.8, 1.4, 'south');
    atlasMesh(p, kit, material, 'backdrop-south-atlas');
    const root = new THREE.Group();
    root.add(p);
    return { root };
  },
};
